import { IAPIResult, IDefaultAPIResult } from 'types/state.interface';
import {
  IAuthConfig,
  TAffiliation,
  TAffiliationFindPayload,
} from '@timeedit/types';
import { baseApi } from '../../../../api/baseApi';

const tagsFromConfigIdParam = (
  _result: unknown,
  _error: unknown,
  params: { authConfigId: string }
) => [{ type: 'AuthAffiliations' as const, id: params.authConfigId }];

const tagsFromAffiliationIdParam = (
  _result: unknown,
  _error: unknown,
  params: { authConfigId: string; affiliationId: string }
) => [
  {
    type: 'AuthAffiliations' as const,
    id: `${params.authConfigId}-${params.affiliationId}`,
  },
];

const tagsFromBoth = (
  result: unknown,
  error: unknown,
  params: { authConfigId: string; affiliationId: string }
) =>
  tagsFromConfigIdParam(result, error, params).concat(
    tagsFromAffiliationIdParam(result, error, params)
  );

export const authConfigApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAuthConfigs: build.query<IAuthConfig[], void>({
      query: () => `/auth-configs`,
      transformResponse: (response: IDefaultAPIResult<IAuthConfig>) =>
        response.results,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'AuthConfig' as const,
                id: id ?? 'unknown',
              })),
              { type: 'AuthConfig' as const, id: 'LIST' },
            ]
          : [{ type: 'AuthConfig' as const, id: 'LIST' }],
    }),

    getConfig: build.query<IAuthConfig, { authConfigId: string }>({
      query: ({ authConfigId }) => `/auth-configs/${authConfigId}`,
      providesTags: (_data, _error, params) => [
        { type: 'AuthConfig', id: params.authConfigId },
      ],
    }),
    createAffiliation: build.mutation<
      TAffiliation,
      {
        authConfigId: string;
        affiliation: Omit<TAffiliation, 'authConfigId'>;
        // Lets batch flows invalidate once at the end instead of
        // refetching the list on every row.
        skipInvalidation?: boolean;
      }
    >({
      query: ({ authConfigId, affiliation }) => ({
        method: 'POST',
        url: `/auth-configs/${authConfigId}/affiliations`,
        body: affiliation,
      }),
      invalidatesTags: (result, error, params) =>
        params.skipInvalidation
          ? []
          : tagsFromConfigIdParam(result, error, params),
    }),

    findAffiliations: build.query<
      IAPIResult<TAffiliation>,
      TAffiliationFindPayload & { authConfigId: string }
    >({
      query: ({ authConfigId, ...body }) => ({
        method: 'POST',
        url: `/auth-configs/${authConfigId}/affiliations/find`,
        body,
      }),
      providesTags: tagsFromConfigIdParam,
    }),

    countAffiliations: build.query<{ total: number }, { authConfigId: string }>(
      {
        query: ({ authConfigId }) =>
          `/auth-configs/${authConfigId}/affiliations/count`,
        providesTags: tagsFromConfigIdParam,
      }
    ),

    getAffiliation: build.query<
      TAffiliation,
      { authConfigId: string; affiliationId: string }
    >({
      query: (params) =>
        `/auth-configs/${params.authConfigId}/affiliations/${params.affiliationId}`,
      providesTags: tagsFromAffiliationIdParam,
    }),

    patchAffiliation: build.mutation<
      TAffiliation,
      {
        authConfigId: string;
        affiliationId: string;
        affiliation: Partial<Omit<TAffiliation, 'authConfigId'>>;
      }
    >({
      query: (params) => ({
        method: 'PATCH',
        url: `/auth-configs/${params.authConfigId}/affiliations/${params.affiliationId}`,
        body: params.affiliation,
      }),
      invalidatesTags: tagsFromBoth,
    }),

    deleteAffiliation: build.mutation<
      void,
      { authConfigId: string; affiliationId: string }
    >({
      query: (params) => ({
        method: 'DELETE',
        url: `/auth-configs/${params.authConfigId}/affiliations/${params.affiliationId}`,
      }),
      // Only invalidate the list — invalidating the affiliation's own tag
      // would force `useGetAffiliationQuery` to refetch the now-deleted
      // entity and 404.
      invalidatesTags: tagsFromConfigIdParam,
    }),

    bulkDeleteAffiliations: build.mutation<
      void,
      {
        authConfigId: string;
        filters?: TAffiliationFindPayload['filters'] & {
          ids?: string[];
          excludedIds?: string[];
        };
        searchText?: string;
      }
    >({
      query: ({ authConfigId, ...body }) => ({
        method: 'POST',
        url: `/auth-configs/${authConfigId}/affiliations/bulk-delete`,
        body,
      }),
      invalidatesTags: tagsFromConfigIdParam,
    }),
  }),
});

export const {
  useBulkDeleteAffiliationsMutation,
  useGetAuthConfigsQuery,
  useGetConfigQuery,
  useCreateAffiliationMutation,
  useLazyFindAffiliationsQuery,
  useCountAffiliationsQuery,
  useGetAffiliationQuery,
  useDeleteAffiliationMutation,
  usePatchAffiliationMutation,
  useFindAffiliationsQuery,
} = authConfigApi;
